import React, { useContext, useEffect } from "react";
import { Box, Button, IconButton } from "@mui/material";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "AuthContextWrapper"
import { OUTCOMES } from "../../../constants";
import { AddressAutocompleteForm } from "../shared/AddressAutocompleteForm";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { UserProfileAPI } from "apis/UserProfileAPI";
import { Place } from "models/Place";
import { ExecutorVerificationContext } from "ExecutorVerificationContextWrapper";
import { Loading } from "views/shared/Loading";
import { EXECUTOR_VERIFICATION_NOT_STARTED_ID, EXECUTOR_VERIFICATION_IN_PROGRESS_ID, EXECUTOR_VERIFICATION_STEP_BASIC_INFO_ID } from ".";

export const AboutYouAddress = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow, user, setUser } = useAuthContext()
  const { userProfile, loadingUserProfile, setUserProfile, getStepStatusId, setStepStatusId, verificationStepId, setVerificationStepId } = useContext(ExecutorVerificationContext);
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [place, setPlace] = React.useState<Place|undefined>(undefined);

  useEffect(() => {
    setVerificationStepId(EXECUTOR_VERIFICATION_STEP_BASIC_INFO_ID)
  }, [])

  const handleBack = async (): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    flow?.navigateToNext(currentScreen, OUTCOMES.back, navigate);
    return true;
  };
  
  const handleComplete = async (place?:Place):  Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    if(loadingUserProfile || !userProfile) {
      return false
    }

    setIsSubmitting(true);

    try {
      if(place && place.formattedAddress) {
        userProfile.addressOne = place.formattedAddress
        userProfile.zipCode = place.postal_code
        setUserProfile(userProfile) 
  
        await UserProfileAPI.update(userProfile)
  
        if(user && setUser) {
          user.profile = userProfile
          setUser(user)
        }
      }
  
      if(getStepStatusId(verificationStepId) === EXECUTOR_VERIFICATION_NOT_STARTED_ID) {
        await setStepStatusId(verificationStepId, EXECUTOR_VERIFICATION_IN_PROGRESS_ID)
      }
    } catch (error) {
      console.error("issue updating step, " + (error as any)?.message)
      setIsSubmitting(false)
      return false
    }
    
    setIsSubmitting(false)
    
    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  if (!currentScreen) {
    return <></>;
  }

  return (
    <FlowScreenWrapper
      title="Where do you live (for tax and legal purposes)?"
      subTitle={
        "Please provide the address of your main residence or in the state where you pay taxes. Please note that this may or may not be the same address as the one on your driver’s license."
      }
    >
      <div>
        <Box sx={{ mb: 2 }}>
          {(isSubmitting || loadingUserProfile) && <Loading />}
          {(!isSubmitting && !loadingUserProfile) &&
            <AddressAutocompleteForm 
              initAddress={place?.formattedAddress || userProfile?.addressOne || ""}
              inputLabelOnTop={true}
              onComplete = {async (place:Place) => {
                setPlace(place)
              }}
              onClear={() => {
                setPlace(undefined)
              }}
              clearButtonVariant ="outlined"
            />}
        </Box>
        <Box sx={{ my: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row-reverse",
              justifyContent: "space-between",
            }}
          >
            <Button
              color={"secondary"}
              variant="contained"
              disabled={isSubmitting}
              sx={{ minWidth: 180 }}
              onClick={async () => {
                await handleComplete(place);
              }}
            >
              Continue
            </Button>
            <IconButton
              color={"secondary"}
              disabled={isSubmitting}
              aria-label="back"
              onClick={handleBack}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon color={"secondary"} />
            </IconButton>
          </Box>
        </Box>
      </div>
    </FlowScreenWrapper>
  );
};
